import { useCallback, useEffect, useRef, useState } from 'react';
import Btn from '../shared/Btn';
import Field from '../shared/Field';
import Textarea from '../shared/Textarea';
import LetterSectionsPanel from '../../letterSections/LetterSectionsPanel';
import { generateLMNLetter } from '../../../api/claimsClient';

/**
 * LMN letter surface (LTR-08). Thin host around the shared structured-letter
 * panel: the legacy single-textarea editor, per-blob Save, and manual PDF
 * download are gone — per-section editing, autosave, approval, PDF preview and
 * final download now live inside LetterSectionsPanel (letterType="lmn").
 *
 * This host keeps the LMN-specific controls the panel does not own:
 *  - whole-letter refinement regenerate (free-text instructions), and
 *  - Delete letter.
 * Per-section regenerate is wired to the same LMN generate endpoint with
 * section_keys=[key].
 *
 * CLAUDE.md effect safety: sectionsRefreshKey bumps exactly once when the
 * polled letter status transitions INTO 'generated' (previous-status ref) so a
 * completed (re)generation refetches sections once — no callback-driven loop.
 * Callbacks live in refs; the section fetch is keyed by the letter id + that
 * refresh key inside the panel.
 *
 * @param {{
 *   letter: any,
 *   onRegenerateStarted: (letterId: string) => void,
 *   onDirtyChange?: (dirty: boolean) => void,
 *   onDeleteLMN?: (letterId: string) => void,
 * }} props
 */
export default function LMNEditorPanel({
  letter, onRegenerateStarted, onDirtyChange, onDeleteLMN,
}) {
  const letterId = letter?.id;
  const status = letter?.status;
  // Treat unknown/unpolled status as generating: when the editor opens right after
  // "Start LMN generation", the poller hasn't returned yet (status undefined), and
  // the letter's sections don't exist for ~30-60s. Only 'generated'/'failed' are
  // terminal — everything else shows the generating state (no premature section fetch).
  const isFailed = status === 'failed';
  const isGenerating = !isFailed && status !== 'generated';

  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(/** @type {string|null} */ (null));
  const [refreshKey, setRefreshKey] = useState(0);
  const prevStatusRef = useRef(status);

  const onRegenerateStartedRef = useRef(onRegenerateStarted);
  onRegenerateStartedRef.current = onRegenerateStarted;

  // Bump the panel refresh key once on the generating -> generated edge.
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev === 'generating' && status === 'generated') {
      setRefreshKey((k) => k + 1);
    }
  }, [status]);

  // The panel autosaves each section itself, so this host has no whole-letter
  // dirty state to surface for a close-confirm — report clean once on mount.
  useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  const generationStatus = isGenerating
    ? 'generating'
    : status === 'generated'
      ? 'generated'
      : undefined;

  const handleRegenerateSection = useCallback(
    async (sectionKey) => {
      if (!letterId) return;
      setAlert(null);
      try {
        await generateLMNLetter(letterId, { section_keys: [sectionKey] });
        onRegenerateStartedRef.current?.(letterId);
      } catch (/** @type {any} */ e) {
        setAlert(e?.response?.data?.error || e?.message || 'Regenerate failed.');
      }
    },
    [letterId]
  );

  const onRefine = async () => {
    if (isGenerating) return;
    if (!isFailed && !instructions.trim()) return;
    setBusy(true);
    setAlert(null);
    try {
      await generateLMNLetter(letterId, { refinement_instructions: instructions });
      onRegenerateStartedRef.current?.(letterId);
      setInstructions('');
    } catch (/** @type {any} */ e) {
      setAlert(e?.response?.data?.error || e?.message || 'Regenerate failed.');
    } finally {
      setBusy(false);
    }
  };

  const refineDisabled = busy || isGenerating || (!isFailed && !instructions.trim());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {isFailed && letter?.error_message && (
        <div
          role="alert"
          style={{
            background: 'var(--ab-denied-soft, #fee)',
            color: 'var(--ab-denied, #900)',
            padding: 8,
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          {letter.error_message}
        </div>
      )}

      <LetterSectionsPanel
        letterType="lmn"
        letterId={letterId}
        sectionsRefreshKey={refreshKey}
        generationStatus={generationStatus}
        onRegenerateSection={handleRegenerateSection}
        onAlert={setAlert}
      />

      <Field label="Additional instructions">
        <Textarea
          aria-label="Instructions"
          rows={4}
          value={instructions}
          onChange={(/** @type {any} */ e) => setInstructions(e.target.value)}
          placeholder="What should change across the whole letter? e.g., Add reference to MRI from 2026-03-04."
        />
      </Field>
      <div>
        <Btn kind="primary" onClick={onRefine} disabled={refineDisabled}>
          {isFailed ? 'Retry' : 'Regenerate'}
        </Btn>
        {isGenerating && (
          <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ab-fg-subtle)' }}>
            Regenerating… you can close this and we'll let you know when it's done.
          </span>
        )}
      </div>

      {alert && (
        <div role="alert" style={{ color: 'var(--ab-denied, #900)', fontSize: 12 }}>
          {alert}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Delete this Letter of Medical Necessity? This cannot be undone.')) {
              onDeleteLMN?.(letterId);
            }
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--ab-denied, #b00020)',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 'var(--ab-weight-semi)',
            padding: '4px 8px',
          }}
        >
          Delete letter
        </button>
      </div>
    </div>
  );
}
